import { FC, useEffect } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { getContactDashboard } from "../../action";
import store, { RootState } from "../../../../../store";
import { formatConcepts, getQueryContactId } from "utils";
import { Col, Row } from "antd";
import ContactDetailsCard from "./ContactDetailsCard";
import CampaignsListing from "./CampaignsListing";
import BubbleChart from "modules/Analytics/Charts/BubbleChart";
import WordCloudGraph from "modules/Analytics/Charts/WordCloudGraph";
import DonutChart from "modules/Content/Shared/DonutChart";
import ContactsDashboardLoader from "./ContactsDashboardLoader";
import { getQueryAllCampaigns } from "modules/EmailBrowser/utils";
import { selectAllCampaigns, setCampaignsFilter } from "modules/Analytics/Dashboard/actions";
import { ANALYTICS_DASHBOARD } from "constants/types";
import { moveSelectedFiltersToApplied } from "modules/Analytics/actions";

const Highcharts = require("highcharts");
require("highcharts/modules/exporting")(Highcharts);
require("highcharts/highcharts-more")(Highcharts);

interface IContactsDashboard {
  page?: string;
  tab?: string;
}

const DEFAULT_PAGE_LIMIT = 20;

const ContactsDashboard: FC<IContactsDashboard> = (props: IContactsDashboard) => {
  //Redux states
  const loading = useSelector((state: RootState) => state.contactInnerDetails.dashboardLoading);
  const dashboard = useSelector((state: RootState) => state.contactInnerDetails.dashboard);
  const keywords = useSelector(
    (state: RootState) => state.contactInnerDetails.dashboard?.contact_intent_data?.keywords
  );
  const entities = useSelector(
    (state: RootState) => state.contactInnerDetails.dashboard?.contact_intent_data?.entities
  );
  const concepts = useSelector(
    (state: RootState) => state.contactInnerDetails.dashboard?.contact_intent_data?.concepts
  );
  const page = useSelector((state: RootState) => state.contactInnerDetails.dashboard.campaignsPage);
  const contactDetails = useSelector((state: RootState) => state.contactInnerDetails.dashboard.contact_details);

  const getPagination = () => {
    return {
      limit: DEFAULT_PAGE_LIMIT,
      contact_id: getQueryContactId(),
      offset: page > 0 ? dashboard.contact.length : 0,
      emailIds: [1],
      campaignIds: [1],
    };
  };

  useEffect(() => {
    const isAllCampaigns = getQueryAllCampaigns();
    if (isAllCampaigns === 'true') {
      setCampaignsFilter({ isAllCampaigns: true }, true);
      selectAllCampaigns();
      store.dispatch({
        type: ANALYTICS_DASHBOARD.SET_CAMPAIGN_FILTERS,
      });
      moveSelectedFiltersToApplied();
    }
    if (Object.keys(dashboard).length <= 0) getContactDashboard(getPagination());
  }, []);
  return (
    <>
      {loading ? (
        <ContactsDashboardLoader loading={loading} />
      ) : (
        <Wrapper>
          <Row gutter={24}>
            <Col span={8}>{dashboard?.contact_details !== undefined && <ContactDetailsCard />}</Col>
            <Col span={16}>
              {!loading &&
                <Row gutter={24}>
                  <Col span={16}>
                    <h3 className="heading-profile">Campaigns Subscribed</h3>
                  </Col>
                  <Col span={8}>
                    <span className="total-delivered">
                      <span className="total-delivered-text">Total Sent (Delivered%) : </span>
                      <span className="total-delivered-numericals">{contactDetails?.total_sent} ({contactDetails?.delivery_rate})</span>
                    </span>
                  </Col>
                </Row>
              }
              <CampaignsListing />
            </Col>
          </Row>

          {concepts?.length === 0 && keywords?.length === 0 && entities?.length === 0 ? (
            <h2 className="d-flex justify-content-center align-items-center">
              Intent data (Concepts, Keywords, and Entities) will be shown here
            </h2>
          ) : (
            <Row gutter={24} className="my-4">
              {concepts && concepts.length > 0 && (
                <Col span={8}>
                  <DonutChart
                    data={formatConcepts(concepts)}
                    backgroundColor={"white"}
                    title={"CONCEPTS"}
                    width={530}
                    height={400}
                    fontSize={28}
                    fontStyle={"Roboto Slab"}
                    highcharts={Highcharts}
                  />
                </Col>
              )}
              {keywords && keywords.length > 0 && (
                <Col span={8}>
                  <WordCloudGraph data={keywords} />
                </Col>
              )}
              {entities && entities.length > 0 && (
                <Col span={8}>
                  <BubbleChart
                    data={entities}
                    highcharts={Highcharts}
                    backgroundColor={"white"}
                    width={630}
                    height={430}
                  />
                </Col>
              )}
            </Row>
          )}
        </Wrapper>
      )}
    </>
  );
};

export default ContactsDashboard;

const Wrapper = styled.div`
  float: left;
  width: 100%;
  display: block;
  margin-top: 15px;
  .total-delivered {
    font-family: "Roboto Slab";
    white-space: nowrap;
    font-size: 18px;
    font-weight: 500;
    line-height: 30px;
    letter-spacing: 0px;
    float: right;
    white-space: nowrap;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 16px;
      line-height: 20px;
    }
    .total-delivered-text {
      color: #424B50;
    }
    .total-delivered-numericals {
      color: #1EC252;
    }
  }
  .heading-profile {
    width: 100%;
    color: #424b50;
    font-size: 23px;
    font-family: "Roboto Slab", serif;
    font-weight: 400;
    margin-bottom: 24px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 16px;
    }
  }
  h2 {
    padding-top: 150px;
    color: #cacaca;
    font-size: 23px;
    font-weight: 400;
    font-family: "Roboto Slab", serif;
    line-height: 32px;
    letter-spacing: -0.5px;
    margin-bottom: 100px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 16px;
      line-height: 23px;
      margin-bottom: 40px;
    }
  }
`;
